import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { StockNews } from "../hooks/useStockNews";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const publicationTime = (value: string) => {
  try {
    // Match the dashboard: FMP's timestamps without an offset are Eastern time.
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)) return dayjs(value);
    return dayjs.tz(value, "America/New_York");
  } catch {
    return dayjs(value);
  }
};

interface StockNewsArticleProps {
  article: StockNews;
  lead?: boolean;
}

export const StockNewsArticle: React.FC<StockNewsArticleProps> = ({
  article,
  lead = false,
}) => {
  const theme = useTheme();
  const accent =
    theme.palette.mode === "dark"
      ? theme.palette.primary.main
      : darken(theme.palette.primary.main, 0.35);
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const hasImage = Boolean(article.image) && failedImage !== article.image;
  const published = publicationTime(article.publishedDate);
  const hasDate = published.isValid();
  const publisher = article.publisher?.trim();
  const source = publisher ? publisher : article.site;
  const desktopImageWidth = lead ? "minmax(180px, 32%)" : "128px";
  const mobileAreas = lead ? '"image" "content"' : '"content image"';

  return (
    <Box component="article" sx={{ minWidth: 0 }}>
      <Box
        component="a"
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${article.title} (opens in a new tab)`}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: hasImage && !lead ? "minmax(0, 1fr) 72px" : "minmax(0, 1fr)",
            sm: hasImage
              ? `${desktopImageWidth} minmax(0, 1fr)`
              : "minmax(0, 1fr)",
          },
          gridTemplateAreas: {
            xs: hasImage ? mobileAreas : '"content"',
            sm: hasImage ? '"image content"' : '"content"',
          },
          alignItems: "center",
          gap: { xs: 1.5, sm: 2.5 },
          p: { xs: 1, sm: 1.5 },
          borderRadius: 1.25,
          color: "text.primary",
          textDecoration: "none",
          minWidth: 0,
          WebkitTapHighlightColor: "transparent",
          transition: "background-color 180ms ease",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          },
          "@media (hover: hover)": {
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.045),
              "& .news-headline": { color: accent },
              "& .news-image": { transform: "scale(1.035)", opacity: 1 },
            },
          },
          "&:active": { bgcolor: "action.selected" },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "& .news-headline, & .news-image": {
              transition: "none",
              transform: "none !important",
            },
          },
        }}
      >
        {hasImage && (
          <Box
            sx={{
              gridArea: "image",
              overflow: "hidden",
              borderRadius: 1,
              width: "100%",
              aspectRatio: { xs: lead ? "16 / 9" : "1", sm: "3 / 2" },
              maxHeight: lead ? 240 : 96,
              alignSelf: lead ? "center" : "start",
              bgcolor: "action.hover",
            }}
          >
            <Box
              component="img"
              className="news-image"
              src={article.image}
              alt=""
              loading={lead ? "eager" : "lazy"}
              decoding="async"
              onError={() => setFailedImage(article.image)}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.92,
                transition: "transform 240ms ease, opacity 180ms ease",
              }}
            />
          </Box>
        )}
        <Box sx={{ gridArea: "content", minWidth: 0, py: { xs: 0, sm: 0.5 } }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: 1,
              rowGap: 0.25,
              mb: { xs: 0.75, sm: 1 },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: accent,
                overflowWrap: "anywhere",
              }}
            >
              {source}
            </Typography>
            {hasDate && (
              <Typography
                component="time"
                dateTime={published.toISOString()}
                title={published.local().format("MMM D, YYYY [at] h:mm A")}
                sx={{
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {published.fromNow()}
              </Typography>
            )}
          </Box>
          <Typography
            component="h3"
            className="news-headline"
            sx={{
              fontSize: {
                xs: lead ? "1.0625rem" : "0.875rem",
                sm: lead ? "1.375rem" : "1rem",
                lg: lead ? "1.5rem" : "1.0625rem",
              },
              fontWeight: 600,
              lineHeight: 1.4,
              letterSpacing: lead ? "-0.015em" : "normal",
              overflowWrap: "anywhere",
              display: "-webkit-box",
              WebkitLineClamp: lead ? 4 : 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              transition: "color 180ms ease",
            }}
          >
            {article.title}
          </Typography>
          {article.text && (
            <Typography
              sx={{
                mt: { xs: 0.75, sm: 1 },
                color: "text.secondary",
                fontSize: {
                  xs: "0.8125rem",
                  sm: lead ? "0.875rem" : "0.8125rem",
                },
                lineHeight: 1.6,
                maxWidth: "100ch",
                display: {
                  xs: lead ? "-webkit-box" : "none",
                  sm: "-webkit-box",
                },
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {article.text}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
